import type { Hero } from '../types';
import {
  calculateCounterBonus,
  calculateSynergyBonus,
  calculateWinRate,
} from './winRateModel';

export interface CounterReasonEntry {
  opponentId: number;
  opponentName: string;
  opponentHeadUrl: string | null;
  value: number;
}

export interface SynergyReasonEntry {
  partnerId: number;
  partnerName: string;
  partnerHeadUrl: string | null;
  value: number;
}

export interface WinRateDeltaDetail {
  totalDelta: number;
  baseDelta: number;
  counterReasons: CounterReasonEntry[];
  synergyReasons: SynergyReasonEntry[];
  /** クランプ等で線形分解とずれる分（totalDelta と表示行の整合用） */
  residualDelta: number;
}

type DeltaPartsOnly = Omit<WinRateDeltaDetail, 'residualDelta'>;

function sumReasonLines(d: DeltaPartsOnly): number {
  let s = d.baseDelta;
  for (const r of d.counterReasons) {
    s += r.value;
  }
  for (const r of d.synergyReasons) {
    s += r.value;
  }
  return s;
}

function finalizeDetail(partial: DeltaPartsOnly): WinRateDeltaDetail {
  const residualDelta = partial.totalDelta - sumReasonLines(partial);
  return { ...partial, residualDelta };
}

export function calculateWinRateDeltaDetail(
  hero: Hero,
  currentAllyHeroes: Hero[],
  currentEnemyHeroes: Hero[],
  isAddingToAlly: boolean
): WinRateDeltaDetail {
  if (isAddingToAlly) {
    const beforeWinRate = calculateWinRate(currentAllyHeroes, currentEnemyHeroes);
    const afterWinRate = calculateWinRate([...currentAllyHeroes, hero], currentEnemyHeroes);
    const totalDelta = afterWinRate - beforeWinRate;
    const baseDelta = hero.win_rate - 0.5;

    const counterReasons: WinRateDeltaDetail['counterReasons'] = [];
    for (const enemy of currentEnemyHeroes) {
      const bonus = calculateCounterBonus(hero, enemy);
      if (bonus !== 0) {
        counterReasons.push({
          opponentId: enemy.id,
          opponentName: enemy.name,
          opponentHeadUrl: enemy.head_url,
          value: bonus,
        });
      }
    }

    const synergyReasons: WinRateDeltaDetail['synergyReasons'] = [];
    for (const ally of currentAllyHeroes) {
      const bonus = calculateSynergyBonus(hero, ally);
      if (bonus !== 0) {
        synergyReasons.push({
          partnerId: ally.id,
          partnerName: ally.name,
          partnerHeadUrl: ally.head_url,
          value: bonus,
        });
      }
    }

    return finalizeDetail({ totalDelta, baseDelta, counterReasons, synergyReasons });
  }

  const beforeWinRate = calculateWinRate(currentAllyHeroes, currentEnemyHeroes);
  const afterWinRate = calculateWinRate(currentAllyHeroes, [...currentEnemyHeroes, hero]);
  const beforeEnemyRate = 1 - beforeWinRate;
  const afterEnemyRate = 1 - afterWinRate;
  const totalDelta = afterEnemyRate - beforeEnemyRate;
  const baseDelta = hero.win_rate - 0.5;

  const counterReasons: WinRateDeltaDetail['counterReasons'] = [];
  for (const opponent of currentAllyHeroes) {
    const bonus = calculateCounterBonus(opponent, hero);
    const enemyPerspectiveBonus = -bonus;
    if (enemyPerspectiveBonus !== 0) {
      counterReasons.push({
        opponentId: opponent.id,
        opponentName: opponent.name,
        opponentHeadUrl: opponent.head_url,
        value: enemyPerspectiveBonus,
      });
    }
  }

  const synergyReasons: WinRateDeltaDetail['synergyReasons'] = [];
  for (const enemy of currentEnemyHeroes) {
    const bonus = calculateSynergyBonus(hero, enemy);
    if (bonus !== 0) {
      synergyReasons.push({
        partnerId: enemy.id,
        partnerName: enemy.name,
        partnerHeadUrl: enemy.head_url,
        value: bonus,
      });
    }
  }

  return finalizeDetail({ totalDelta, baseDelta, counterReasons, synergyReasons });
}

export function calculateWinRateDelta(
  hero: Hero,
  currentAllyHeroes: Hero[],
  currentEnemyHeroes: Hero[],
  isAddingToAlly: boolean
): number {
  if (isAddingToAlly) {
    const beforeWinRate = calculateWinRate(currentAllyHeroes, currentEnemyHeroes);
    const afterWinRate = calculateWinRate([...currentAllyHeroes, hero], currentEnemyHeroes);
    return afterWinRate - beforeWinRate;
  }
  const beforeWinRate = calculateWinRate(currentAllyHeroes, currentEnemyHeroes);
  const afterWinRate = calculateWinRate(currentAllyHeroes, [...currentEnemyHeroes, hero]);
  const beforeEnemyRate = 1 - beforeWinRate;
  const afterEnemyRate = 1 - afterWinRate;
  return afterEnemyRate - beforeEnemyRate;
}
